import cx from "classnames";
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

export type HomeComposerMode = "chat" | "project";

/** Fast-out / settle-in curve for the mode thumb and project lane. */
export const HOME_COMPOSER_SLIDE_EASING =
  "cubic-bezier(0.16, 1, 0.3, 1)" as const;

const MODES: HomeComposerMode[] = ["chat", "project"];

/** Matches track `p-0.5` and thumb `top-0.5` / `bottom-0.5` — keeps the pill inset even. */
const TRACK_INSET_PX = 2;

/**
 * Home-only Chat / Project segmented control. Height and radius match the
 * Ask Puntt submit button (`size="2"` + `rounded-xl`); each segment sizes to
 * its label rather than stretching to a fixed equal width. A sliding thumb
 * tracks the active segment above the gray track (labels stay above the thumb).
 */
export function HomeComposerModeToggle({
  value,
  onChange,
  testIdPrefix = "ask-puntt-home",
  "data-testid": dataTestId = "ask-puntt-home-composer-mode",
}: {
  value: HomeComposerMode;
  onChange: (mode: HomeComposerMode) => void;
  /** Prefix for segment + thumb testids when a second toggle is on screen. */
  testIdPrefix?: string;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const trackRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLButtonElement>(null);
  const projectRef = useRef<HTMLButtonElement>(null);
  const [thumb, setThumb] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const track = trackRef.current;
    const activeEl = value === "chat" ? chatRef.current : projectRef.current;
    if (!track || !activeEl) return;

    const measure = () => {
      const trackBox = track.getBoundingClientRect();
      const activeBox = activeEl.getBoundingClientRect();
      const segLeft = activeBox.left - trackBox.left;
      const segRight = activeBox.right - trackBox.left;
      // Clamp to track padding so horizontal inset matches vertical (Project’s
      // segment can sit flush to the track edge without the 2px pad).
      const left = Math.max(TRACK_INSET_PX, segLeft);
      const right = Math.min(trackBox.width - TRACK_INSET_PX, segRight);
      setThumb({
        left,
        width: Math.max(0, right - left),
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    observer.observe(activeEl);
    return () => observer.disconnect();
  }, [value]);

  const focusMode = (mode: HomeComposerMode) => {
    const el = mode === "chat" ? chatRef.current : projectRef.current;
    el?.focus();
  };

  const selectMode = (mode: HomeComposerMode) => {
    if (mode === value) return;
    onChange(mode);
    focusMode(mode);
  };

  const handleSegmentKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = MODES.indexOf(value);
    if (index < 0) return;

    let next: HomeComposerMode | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = MODES[(index + 1) % MODES.length] ?? null;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = MODES[(index - 1 + MODES.length) % MODES.length] ?? null;
        break;
      case "Home":
        next = "chat";
        break;
      case "End":
        next = "project";
        break;
      default:
        break;
    }
    if (next == null) return;
    event.preventDefault();
    selectMode(next);
  };

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={t("home.composerModeAriaLabel")}
      data-testid={dataTestId}
      className="relative inline-flex h-8 items-stretch overflow-hidden rounded-xl bg-puntt-neutral-gray-3 p-0.5"
    >
      <span
        aria-hidden="true"
        data-testid={`${testIdPrefix}-composer-mode-thumb`}
        className="pointer-events-none absolute inset-y-0.5 z-[1] rounded-[10px] bg-base-white shadow-sm motion-reduce:transition-none"
        style={{
          left: thumb.left,
          width: thumb.width,
          transition: `left 220ms ${HOME_COMPOSER_SLIDE_EASING}, width 220ms ${HOME_COMPOSER_SLIDE_EASING}`,
        }}
      />
      <ModeSegment
        ref={chatRef}
        data-testid={`${testIdPrefix}-mode-chat`}
        label={t("home.composerModeChat")}
        active={value === "chat"}
        onSelect={() => onChange("chat")}
        onKeyDown={handleSegmentKeyDown}
      />
      <ModeSegment
        ref={projectRef}
        data-testid={`${testIdPrefix}-mode-project`}
        label={t("home.composerModeProject")}
        active={value === "project"}
        onSelect={() => onChange("project")}
        onKeyDown={handleSegmentKeyDown}
      />
    </div>
  );
}

const ModeSegment = forwardRef<
  HTMLButtonElement,
  {
    "data-testid": string;
    label: string;
    active: boolean;
    onSelect: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  }
>(function ModeSegment(
  { "data-testid": testId, label, active, onSelect, onKeyDown },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      data-testid={testId}
      aria-checked={active}
      tabIndex={active ? 0 : -1}
      data-state={active ? "on" : "off"}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cx(
        // Hug label width — do not stretch to equal segments.
        // Transparent so the sliding thumb (z-[1]) stays visible; labels stay above.
        // Without bg-transparent, UA/button gray fills hide the thumb completely.
        "relative z-[2] h-full shrink-0 cursor-pointer rounded-[10px] border-0 bg-transparent px-2.5 text-sm font-medium transition-colors duration-200 motion-reduce:transition-none",
        active
          ? "text-puntt-neutral-gray-12"
          : "text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-12",
      )}
    >
      {label}
    </button>
  );
});
